import {timezones} from '@constants';

type TimezoneDefinition = {
  label: string;
  value: string;
};

const timezoneDefinitions = timezones as TimezoneDefinition[];

const findTimezoneDefinition = (value: string) =>
  timezoneDefinitions.find(
    (timezone) => timezone.label === value || timezone.value === value,
  );

const getLegacyTimezoneName = (value?: string) =>
  value?.replace(/^\(GMT[+-]\d{2}:\d{2}\)\s*/i, '').trim() || '';

export const normalizeTimezoneIdentifier = (value?: string) => {
  const normalizedValue = value?.trim();
  if (!normalizedValue) return '';

  const knownTimezone = findTimezoneDefinition(normalizedValue);
  if (knownTimezone) return knownTimezone.label;

  return normalizedValue;
};

export const serializeTimezoneForApi = (value?: string) => {
  const normalizedValue = value?.trim();
  if (!normalizedValue) return '';

  return findTimezoneDefinition(normalizedValue)?.value || normalizedValue;
};

export const getDetectedTimezone = () => {
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return normalizeTimezoneIdentifier(detectedTimezone) || 'UTC';
};

export const getTimezoneOffset = (timezone: string, date = new Date()) => {
  try {
    const timezoneName = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      timeZoneName: 'longOffset' as Intl.DateTimeFormatOptions['timeZoneName'],
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;

    return timezoneName === 'GMT' ? 'GMT+00:00' : timezoneName || '';
  } catch {
    return '';
  }
};

export const getTimezoneOptions = (date = new Date()) =>
  timezoneDefinitions.map((timezone) => ({
    label: `${timezone.label} (${
      getTimezoneOffset(timezone.label, date) ||
      timezone.value.match(/GMT[+-]\d{2}:\d{2}/i)?.[0] ||
      'GMT'
    })`,
    value: timezone.label,
  }));

export const formatTimezoneForUser = (value?: string, date = new Date()) => {
  const normalizedValue = value?.trim();
  if (!normalizedValue) return '—';

  const definition = findTimezoneDefinition(normalizedValue);
  const identifier = normalizeTimezoneIdentifier(normalizedValue);
  const readableName = definition
    ? getLegacyTimezoneName(definition.value)
    : normalizedValue;
  const offset = identifier ? getTimezoneOffset(identifier, date) : '';

  if (readableName && offset) return `${readableName} (${offset})`;

  const legacyMatch = normalizedValue.match(
    /^(\(GMT[+-]\d{2}:\d{2}\))\s+(.+)$/i,
  );
  return legacyMatch ? `${legacyMatch[2]} ${legacyMatch[1]}` : normalizedValue;
};
